import React, { useEffect, useMemo, useState } from 'react';
import { AddUniversal, MoreInfoOutlined } from '@duxton-web/icons';
import {
  Button,
  Flex,
  notification,
  Select,
  Spin,
  Tag,
  Tooltip,
  Typography,
} from '@duxton-web/ui';

import {
  ControlMetricWithFilters,
  MAX_CONTROL_METRICS,
} from '../../../constants/analysis';
import { constructDimensionFields } from '../../../utils';
import { MetricsWrapper } from '../index.styled';
import MetricRow from '../metric-row';
import { renderPopulationFilterBadges } from '../population-filters';
import {
  ControlMetricCandidate,
  ControlMetricCandidatesResponse,
  generateCandidateId,
  getFilterSummary,
} from './types';
import Service from '@/lib/App';
import * as logger from '@/utils/logging';

const { Option } = Select;

const { Text } = Typography;

const TOOLTIP_MESSAGE =
  'Controlled Metrics are pre-defined, system-level metrics used to stabilize timeslice analysis. They are deselected by default. You can select a maximum of 8 metrics.';

const SectionHeader = () => (
  <Flex align="center" gap={4}>
    <Text strong>Controlled Metrics (C)</Text>
    <Text type="secondary" style={{ fontWeight: 'normal' }}>
      (Optional)
    </Text>
    <Tooltip title={TOOLTIP_MESSAGE}>
      <MoreInfoOutlined style={{ color: '#8c8c8c', cursor: 'help' }} />
    </Tooltip>
  </Flex>
);

interface ControlledMetricsSectionProps {
  controlMetrics: ControlMetricWithFilters[];
  strategy: string;
  filterEnabled: boolean;
  onChange: (controlMetrics: ControlMetricWithFilters[]) => void;
  onSearch: (value: string) => void;
  disabled: boolean;
  showValidation: boolean;
}

const ControlledMetricsSection: React.FC<ControlledMetricsSectionProps> = ({
  controlMetrics,
  strategy,
  filterEnabled,
  onChange,
  onSearch,
  disabled,
  showValidation,
}) => {
  const [candidates, setCandidates] = useState<ControlMetricCandidate[]>([]);
  const [isLoadingCandidates, setIsLoadingCandidates] = useState(true);

  // Fetch control metric candidates on mount
  useEffect(() => {
    let isMounted = true;

    const fetchCandidates = async () => {
      setIsLoadingCandidates(true);
      try {
        const response: ControlMetricCandidatesResponse =
          await Service.planner.fetchControlMetricCandidates();

        if (!isMounted) return;

        // Add unique candidate_id to each candidate
        const candidatesWithIds = (response.candidates || []).map(
          (candidate, index) => ({
            ...candidate,
            candidate_id: generateCandidateId(candidate, index),
          }),
        );
        setCandidates(candidatesWithIds);
      } catch (error) {
        if (!isMounted) return;

        logger.error(error, 'Failed to fetch control metric candidates');
        notification.error({
          message: 'Failed to load control metrics',
          description: 'Please try again later.',
        });
        setCandidates([]);
      } finally {
        if (isMounted) {
          setIsLoadingCandidates(false);
        }
      }
    };

    fetchCandidates();

    return () => {
      isMounted = false;
    };
  }, []);

  // Pass candidates directly to preserve all fields for API submission
  const controlMetricsGlossary = candidates;

  // Generate options for the control metrics selector
  // Shows filter info to distinguish duplicates
  // All options shown - Select component handles highlighting the selected one
  const controlMetricOptions = useMemo(() => {
    return controlMetricsGlossary.map((metric) => {
      const filterSummary = getFilterSummary(metric as ControlMetricCandidate);
      const displayName = metric.metadata?.display_name || metric.name;

      return (
        <Option
          key={metric.candidate_id}
          label={
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Text>{displayName}</Text>
              {metric.version && (
                <Tag size="xsmall" style={{ height: 'fit-content' }}>
                  {`v${metric.version}`}
                </Tag>
              )}
              {filterSummary && (
                <Tag
                  size="xsmall"
                  color="blue"
                  style={{ height: 'fit-content' }}
                >
                  {filterSummary}
                </Tag>
              )}
            </div>
          }
          value={metric.candidate_id}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Text>{displayName}</Text>
              {metric.version && (
                <Tag size="xsmall" style={{ height: 'fit-content' }}>
                  {`v${metric.version}`}
                </Tag>
              )}
            </div>
            {filterSummary && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Filters: {filterSummary}
              </Text>
            )}
          </div>
        </Option>
      );
    });
  }, [controlMetricsGlossary]);

  // Handle adding a new control metric
  const handleAddControlMetric = async () => {
    // Check for unpopulated metric row
    const unpopulatedMetricRowExists = controlMetrics.some(
      (m) => m.name === null,
    );

    if (unpopulatedMetricRowExists) {
      return;
    }

    // Add empty metric slot
    const newMetric = constructDimensionFields({ name: null });
    onChange([...controlMetrics, newMetric]);
  };

  // Handle metric deletion
  const handleControlMetricDeletion = (index: number) => {
    const filteredMetrics = controlMetrics.filter((_, i) => i !== index);
    onChange(filteredMetrics);
  };

  // Handle metric selection using candidate_id
  const handleMetricSelect = (
    metricIndex: number,
    selectedCandidateId: string,
    glossaryItem: any,
  ) => {
    if (!glossaryItem) return;

    const updatedMetrics = [...controlMetrics];
    const newMetric = constructDimensionFields(glossaryItem);
    // Store candidate_id for tracking which specific variant was selected
    updatedMetrics[metricIndex] = {
      ...newMetric,
      candidate_id: selectedCandidateId,
    } as ControlMetricWithFilters;
    onChange(updatedMetrics);
  };

  // Population filters are now applied centrally in the parent Metrics component
  // to ensure synchronized updates across all metric types

  return (
    <MetricsWrapper style={{ marginTop: 8 }}>
      <SectionHeader />

      {isLoadingCandidates ? (
        <Flex justify="center" align="center" style={{ padding: 24 }}>
          <Spin />
        </Flex>
      ) : (
        <>
          {disabled && !controlMetrics?.length ? (
            <div>No selected metrics</div>
          ) : (
            controlMetrics?.map((metric, index) => (
              <React.Fragment
                key={`control-${metric?.candidate_id || metric?.name}-${index}`}
              >
                <MetricRow
                  filterEnabled={filterEnabled}
                  strategy={strategy}
                  metricsGlossaryData={controlMetricsGlossary}
                  metrics={{ control_metrics: controlMetrics }}
                  metricType="control"
                  metricIndex={index}
                  options={controlMetricOptions}
                  onSearch={onSearch}
                  onDelete={() => handleControlMetricDeletion(index)}
                  showValidation={showValidation}
                  prefix="C"
                  // Use candidate_id for unique identification
                  uniqueKeyField="candidate_id"
                  displayValue={metric?.candidate_id}
                  onMetricSelect={(selectedValue, glossaryItem) =>
                    handleMetricSelect(index, selectedValue, glossaryItem)
                  }
                />
                {!disabled && renderPopulationFilterBadges(metric)}
                {showValidation && !metric.name && (
                  <Typography.Text type="danger">
                    Please select a metric
                  </Typography.Text>
                )}
              </React.Fragment>
            ))
          )}

          {!disabled &&
            (controlMetrics?.length >= MAX_CONTROL_METRICS ? (
              <Tooltip
                title={`Maximum limit of ${MAX_CONTROL_METRICS} control metrics!`}
              >
                <Button
                  style={{
                    minWidth: '100%',
                    minHeight: 32,
                    background: '#F5F5F5',
                    border: 'none',
                  }}
                  icon={<AddUniversal />}
                  disabled
                />
              </Tooltip>
            ) : (
              <Button
                style={{
                  minWidth: '100%',
                  minHeight: 32,
                  background: '#F5F5F5',
                  border: 'none',
                }}
                icon={<AddUniversal />}
                onClick={handleAddControlMetric}
              />
            ))}
        </>
      )}
    </MetricsWrapper>
  );
};

export default ControlledMetricsSection;
